import {useState} from "react";
import NiceModal, {useModal} from "@ebay/nice-modal-react";
import {toast} from "sonner";
import {Autocomplete, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import {Box, Colors, Container, Text} from "@bounce/shared/ui";
import {Button, TextField, Tooltip} from "../ui/components";
import {IconChevronUp} from "@bounce/shared/ui/assets";
import {autocompleteChevronSx} from "../ui/components/inputStyles";
import ResourceList from "./ResourceList";
import {ResourcePanel, note, openInNewTab} from "./ResourcePanel";
import {reachUrls} from "../layout/menu-items";
import {STATUS_DISPLAY} from "../pages/templates/templateDisplay";
import {activeTemplates, plural, usageCount, usageSummary} from "../apis/requests/resources/types";
import {useDeleteSnippet} from "../apis/requests/snippets/deleteSnippet";
import {useGetSnippets} from "../apis/requests/snippets/getSnippets";
import {useGetSnippetUsages} from "../apis/requests/snippets/getSnippetUsages";
import {useReplaceSnippetUsages} from "../apis/requests/snippets/replaceSnippetUsages";
import {useDeleteAsset} from "../apis/requests/assets/deleteAsset";
import {useGetAssets} from "../apis/requests/assets/getAssets";
import {useGetAssetUsages} from "../apis/requests/assets/getAssetUsages";
import {useReplaceAssetUsages} from "../apis/requests/assets/replaceAssetUsages";

type ResourceKind = "snippet" | "asset";

type Props = {
  kind: ResourceKind;
  resourceId: number;
  resourceName: string;
  mode?: "delete" | "replace";
  onDeleted?: () => void;
  onClosed?: () => void;
};

type ReplacementOption = {value: string; label: string};

const LABEL: Record<ResourceKind, string> = {snippet: "snippet", asset: "image"};

const copyCase = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

const DeleteResourceDialog = NiceModal.create(
  ({kind, resourceId, resourceName, mode = "delete", onDeleted, onClosed}: Props) => {
  const modal = useModal();
  const isSnippet = kind === "snippet";
  const label = LABEL[kind];
  const replaceOnly = mode === "replace";

  const replaceSnippetMutation = useReplaceSnippetUsages();
  const replaceAssetMutation = useReplaceAssetUsages();
  const deleteSnippetMutation = useDeleteSnippet();
  const deleteAssetMutation = useDeleteAsset();
  const snippetUsages = useGetSnippetUsages({id: resourceId}, {enabled: isSnippet});
  const assetUsages = useGetAssetUsages({id: resourceId}, {enabled: !isSnippet});
  const {data: allSnippets} = useGetSnippets({}, {enabled: isSnippet});
  const {data: allAssets} = useGetAssets({}, {enabled: !isSnippet});

  const {data: usages, isLoading, isError} = isSnippet ? snippetUsages : assetUsages;
  const replaceMutation = isSnippet ? replaceSnippetMutation : replaceAssetMutation;
  const deleteMutation = isSnippet ? deleteSnippetMutation : deleteAssetMutation;

  const [showReplace, setShowReplace] = useState(mode === "replace");
  const [closingAfterReplace, setClosingAfterReplace] = useState(false);
  const [replaceWith, setReplaceWith] = useState("");

  const isBusy = replaceMutation.isPending || deleteMutation.isPending;

  const blocked = usageCount(usages) > 0;
  const active = activeTemplates(usages);
  const templates = usages?.templates ?? [];
  const snippets = usages?.snippets ?? [];
  const translations = usages?.translations ?? [];
  const translationBlocked = translations.length > 0;
  const otherUsageCount = templates.length + snippets.length;
  const blockedNote = translationBlocked
    ? `Still the source of ${usageSummary(usages)}. Point it elsewhere or delete it first.`
    : `Still used by ${usageSummary(usages)}. Replace it first.`;

  const options: ReplacementOption[] = isSnippet
    ? (allSnippets ?? [])
        .filter((snippet) => snippet.id !== resourceId)
        .map((snippet) => ({value: snippet.element_name || snippet.name, label: snippet.name}))
    : (allAssets ?? [])
        .filter((asset) => asset.id !== resourceId)
        .map((asset) => ({value: asset.url, label: asset.name}));

  const replaceUsages = (value: string) =>
    replaceMutation.mutateAsync({
      id: resourceId,
      replace_with: value,
      dry_run: false,
    });

  const runReplace = async () => {
    try {
      const result = await replaceUsages(replaceWith);
      const live = result.updated_active_templates.length;
      toast.success(
        `${copyCase(label)} replaced in ${plural(result.updated_templates.length, "template")}` +
          (result.updated_snippets.length ? ` and ${plural(result.updated_snippets.length, "snippet")}` : "") +
          (live ? ` (${live} live)` : "")
      );
      if (replaceOnly) {
        setClosingAfterReplace(true);
        modal.hide();
        return;
      }
      setShowReplace(false);
      setReplaceWith("");
    } catch {}
  };

  const deleteResource = async () => {
    try {
      await deleteMutation.mutateAsync({id: resourceId});
      toast.success(`${copyCase(label)} "${resourceName}" deleted`);
      onDeleted?.();
      modal.hide();
    } catch {}
  };

  return (
    <Dialog
      open={modal.visible}
      onClose={() => modal.hide()}
      slotProps={{
        transition: {
          onExited: () => {
            modal.remove();
            onClosed?.();
          },
        },
        paper: {sx: {borderRadius: "16px"}},
      }}
      maxWidth="sm"
      fullWidth
      transitionDuration={closingAfterReplace ? 0 : undefined}
    >
      <DialogTitle sx={{p: 3, pb: 0}}>
        <Text size={24} variant="interDisplay600">
          {replaceOnly ? `Replace ${label} everywhere` : `Delete ${label}`}
        </Text>
      </DialogTitle>
      <DialogContent sx={{p: 3}}>
        <Container fullWidth sx={{gap: 2, mt: 1, alignItems: "flex-start"}}>
          {isLoading ? (
            <Box sx={{display: "flex", justifyContent: "center", width: "100%", py: 2}}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Text size={14} variant="inter400" sx={{color: Colors.grey._900, textAlign: "left"}}>
                {replaceOnly
                  ? translationBlocked
                    ? `"${resourceName}" cannot be replaced yet.`
                    : blocked
                      ? `Swap "${resourceName}" for another ${label} everywhere it is used.`
                      : `"${resourceName}" is not used anywhere.`
                  : blocked
                    ? `"${resourceName}" is still in use and cannot be deleted yet.`
                    : `"${resourceName}" is not used anywhere.`}
              </Text>
              {isError &&
                note(
                  `Could not check where this ${label} is used. Reload and try again.`
                )}
              {!isError &&
                note(
                  replaceOnly
                    ? translationBlocked
                      ? `Point the translation at another ${label}, or delete it, before replacing this one.`
                      : blocked
                        ? `Every reference is swapped to the ${label} you pick.`
                        : `There is nothing to replace.`
                    : blocked
                      ? translationBlocked
                        ? otherUsageCount > 0
                          ? `Point the translation at another ${label} first. Replace becomes available once you have.`
                          : `Point the translation at another ${label}, or delete it, and this can be deleted.`
                        : `Replace it everywhere first, then it can be deleted.`
                      : `Deleting it cannot be undone.`
                )}

              {templates.length > 0 && (
                <Container fullWidth sx={{gap: 1, alignItems: "flex-start"}}>
                  {note(`Used by ${plural(templates.length, "template")}:`)}
                  <ResourceList
                    items={templates.map((template) => ({
                      id: String(template.id),
                      label: template.name,
                      variant: STATUS_DISPLAY[template.status]?.variant,
                      tooltip: [
                        STATUS_DISPLAY[template.status]?.label,
                        template.via_snippet ? `uses the "${template.via_snippet}" snippet` : null,
                      ]
                        .filter(Boolean)
                        .join(" - "),
                      onClick: () => openInNewTab(`${reachUrls.templates}/${template.id}`),
                    }))}
                  />
                </Container>
              )}

              {translations.length > 0 && (
                <Container fullWidth sx={{gap: 1, alignItems: "flex-start"}}>
                  {note(`English source of ${plural(translations.length, "translation")}:`)}
                  <ResourceList
                    items={translations.map((translation) => ({
                      id: String(translation.id),
                      label: translation.name,
                      onClick: () => openInNewTab(`${reachUrls.snippets}/${translation.id}`),
                    }))}
                  />
                </Container>
              )}

              {snippets.length > 0 && (
                <Container fullWidth sx={{gap: 1, alignItems: "flex-start"}}>
                  {note(`${templates.length > 0 ? "Also used" : "Used"} inside ${plural(
                      snippets.length,
                      "snippet"
                    )}:`
                  )}
                  <ResourceList
                    items={snippets.map((snippet) => ({
                      id: String(snippet.id),
                      label: snippet.name,
                      onClick: () => openInNewTab(`${reachUrls.snippets}/${snippet.id}`),
                    }))}
                  />
                </Container>
              )}

              {blocked && !translationBlocked && active.length > 0 && (
                <ResourcePanel>
                  {note(`${plural(active.length, "template")} shown in green ${
                      active.length === 1 ? "is" : "are"
                    } live - ${active.length === 1 ? "it" : "they"} will be updated too.`
                  )}
                </ResourcePanel>
              )}

              {blocked && !translationBlocked && showReplace && (
                <Container fullWidth sx={{gap: 1, alignItems: "flex-start"}}>
                  {note(`Pick an existing ${label} to use instead - every reference is swapped to it.`)}
                  <Autocomplete<ReplacementOption, false, false, false>
                    size="small"
                    fullWidth
                    popupIcon={<IconChevronUp style={{width: 20, height: 20}} />}
                    sx={autocompleteChevronSx}
                    options={options}
                    value={options.find((option) => option.value === replaceWith) ?? null}
                    onChange={(_, option) => setReplaceWith(option?.value ?? "")}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    noOptionsText={`No other ${label}s found`}
                    renderInput={(params) => <TextField {...params} placeholder={`Search ${label}s...`} />}
                  />
                </Container>
              )}
            </>
          )}
        </Container>
      </DialogContent>
      <DialogActions sx={{p: 3, pt: 0, justifyContent: "flex-end", gap: 1, flexWrap: "wrap"}}>
        <Button buttonType="outlined-secondary" size="small" onClick={() => modal.hide()} disabled={isBusy}>
          {replaceOnly && !blocked ? "Close" : "Cancel"}
        </Button>
        {blocked && !showReplace && (
          <Tooltip title={translationBlocked ? blockedNote : ""}>
            <span>
              <Button
                buttonType="contained-primary"
                size="small"
                onClick={() => setShowReplace(true)}
                disabled={isBusy || translationBlocked}
              >
                Replace
              </Button>
            </span>
          </Tooltip>
        )}
        {blocked && !translationBlocked && showReplace && (
          <Button
            buttonType="contained-primary"
            size="small"
            onClick={runReplace}
            disabled={isBusy || !replaceWith}
          >
            Replace everywhere
          </Button>
        )}
        {!replaceOnly && (
          <Tooltip title={blocked ? blockedNote : ""}>
            <span>
              <Button
                buttonType="contained-error"
                size="small"
                onClick={deleteResource}
                disabled={isBusy || isLoading || isError || blocked}
              >
                Delete
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>
    </Dialog>
  );
});

export default DeleteResourceDialog;
